import React, { useEffect } from 'react';
import { FormContainer } from 'react-hook-form-mui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Box, Button, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { onboardStyles } from '../components/ComponentStyles';
import SkipConfirmation from '../components/SkipConfirmation';
import DirectFooter from '../components/direct/DirectFooter';
import HelpModal from '../components/onboarding/HelpModal';
import OnboardErrorAlert from '../components/onboarding/OnboardErrorAlert';
import OnboardHeader from '../components/onboarding/OnboardHeader';
import TSPLoginFactory from '../components/onboarding/TSPLoginFactory';
import { useAppState } from '../contexts/AppStateProvider';
import { useJWT } from '../contexts/JWTProvider';
import useOnboardForm from '../hooks/useOnboardForm';
import useSkipOnboarding from '../hooks/useSkipOnboarding';
import { ServiceThemeOptions } from '../themes';
import {
  getTSPHelpTextByKey,
  getTSPImageNameByKey,
  getTSPNameByKey,
} from '../tsps';
import { Analytics, createAnalyticsContext } from '../utils/analytics';
import { SlackTracker, createSlackContext } from '../utils/slackNotifications';

export default function DirectOnboard() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  const location = useLocation();
  const { tsp } = useParams(); // Get TSP from URL
  const { search } = location;

  // Extract theme values
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';
  const textPrimaryColor = theme.palette?.text?.primary || '#000000';
  const textSecondaryColor = theme.palette?.text?.secondary || '#586166';

  // Use new centralized hooks
  const jwt = useJWT();
  const {
    magicLink,
    motiveClientID,
    samsaraClientID,
    zubieClientID,
    bouncieClientID,
    serviceName,
    redirectURIError,
    redirectURISuccess,
    setOnboarded,
    setRedirectURI,
    setTSP,
    setTspCount,
    tspCount,
  } = useAppState();

  // Get JWT-derived properties
  const token = jwt.rawToken || '';
  const isTokenValid = jwt.isValid;
  const fleetCode = jwt.fleetCode || '';
  const serviceId = jwt.serviceId;

  // Help modal state
  const [helpModalOpen, setHelpModalOpen] = React.useState(false);
  const handleHelpClick = React.useCallback(() => {
    setHelpModalOpen(true);
  }, [setHelpModalOpen]);

  // Use the skip onboarding hook
  const {
    skipDialogOpen,
    handleSkipClick,
    handleSkipConfirmed,
    handleCloseSkipDialog,
  } = useSkipOnboarding({
    token,
    redirectURIError,
    redirectURISuccess,
    serviceName,
    tsp: tsp || '',
  });

  // Use the onboard form hook
  const {
    isFormValid,
    setIsFormValid,
    disableButton,
    showError,
    errorText,
    errorDetails,
    setShowError,
    handleFormSubmit,
  } = useOnboardForm({
    TSP: tsp || '',
    serviceName,
    token,
    fleetCode,
    updatingTSP: false,
    serviceID: String(serviceId || ''),
    magicLink,
    redirectURISuccess,
    clientIDs: {
      motiveClientID,
      samsaraClientID,
      zubieClientID,
      bouncieClientID,
    },
    onSuccess: () => {
      setOnboarded(true);
      navigate('/success');
    },
    setRedirectURI,
    setTspCount,
    currentTspCount: tspCount,
  });

  // We don't need OAuth handlers for direct login

  // Set TSP from URL parameter
  useEffect(() => {
    if (tsp) {
      setTSP(tsp);
    }
  }, [tsp, setTSP]);

  // Validate token on mount
  useEffect(() => {
    if (!isTokenValid) {
      // Track error in Slack
      const slackContext = createSlackContext(jwt, serviceName);
      SlackTracker.errorEncountered(
        slackContext,
        'invalid_token',
        'Token validation failed on direct onboard page',
        'DirectOnboard'
      );

      // Navigate to error page immediately for invalid token
      navigate('/error', {
        state: {
          errorMsg: 'Invalid token',
          errorCaption: 'Your session has expired or is invalid.',
          errorType: 'invalid_token',
          showTryAgain: true,
          showSkip: false,
        },
        ...(search && { search }),
      });
    } else {
      // Track page view
      const analyticsContext = createAnalyticsContext(jwt, serviceName, {
        tsp,
        redirectURISuccess,
        redirectURIError,
      });
      Analytics.pageView(analyticsContext, 'DirectOnboard');
    }
  }, [
    isTokenValid,
    navigate,
    tsp,
    serviceName,
    search,
    redirectURIError,
    redirectURISuccess,
    jwt,
  ]);

  // Get TSP info
  const TSPName = getTSPNameByKey(tsp || '') || '';
  const TSPHelpText = getTSPHelpTextByKey(tsp || '');
  const TSPImageName = getTSPImageNameByKey(tsp || '') || '';

  return (
    <>
      <Box sx={onboardStyles.mainContainer}>
        <Paper
          elevation={3}
          sx={{
            ...onboardStyles.paper,
            bgcolor: paperBgColor,
            display: 'flex',
            flexDirection: 'column',
            p: 0, // remove default Paper padding so footer background is flush
            overflow: 'hidden', // ensures footer bg reaches rounded corners
          }}
        >
          {/* CONTENT WRAPPER: reapply the same padding the source uses */}
          <Box
            sx={{
              px: { xs: '16px', sm: '24px' },
              py: { xs: '16px', sm: '24px' },
              flex: '1 1 auto',
            }}
          >
            {/* Header */}
            <OnboardHeader
              onBackClick={() => navigate('/selectTSP', { replace: true })}
              imageName={TSPImageName}
              tsp={tsp || ''}
              serviceName={serviceName}
              backgroundColor={paperBgColor}
            />

            {/* Title/subtitle - use default spacing */}
            <Box sx={onboardStyles.titleContainer}>
              <Typography
                variant='h5'
                align='center'
                sx={{
                  ...onboardStyles.title(theme as any),
                  color: textPrimaryColor,
                }}
              >
                Add your {TSPName} account
              </Typography>
              <Typography
                variant='body2'
                align='center'
                sx={{
                  ...onboardStyles.subtitle(theme as any),
                  color: textSecondaryColor,
                }}
              >
                By providing your {TSPName} credentials to Axle, you're enabling
                Axle to retrieve your telematics data.
              </Typography>
            </Box>

            <FormContainer
              defaultValues={{
                username: '',
                password: '',
                database: '',
                apikey: '',
                authorizationcode: '',
                carriername: '',
                FTPUsername: '',
                FTPPassword: '',
                skipCredentials: false,
              }}
              onSuccess={handleFormSubmit}
              FormProps={{ autoComplete: 'off', id: 'onboarding-form' }}
            >
              <Box sx={onboardStyles.formContainer}>
                {showError && (
                  <OnboardErrorAlert
                    errorText={errorText}
                    helpText={errorDetails?.helpText}
                    showEscapeHatch={errorDetails?.showEscapeHatch}
                    tsp={tsp || ''}
                    serviceName={serviceName}
                    onClose={() => setShowError(false)}
                    onSkip={handleSkipClick}
                    onHelpClick={handleHelpClick}
                  />
                )}

                <Box sx={onboardStyles.loginFormContainer}>
                  <TSPLoginFactory
                    tsp={tsp || ''}
                    onValidityChange={setIsFormValid}
                    TSP={tsp || ''}
                    serviceName={serviceName}
                    onHelpClick={handleHelpClick}
                    oAuthHandlers={{
                      initiateOAuth: () => {}, // direct onboarding doesn't use OAuth
                    }}
                  />

                  {/* CTA - keep identical size/placement */}
                  <Box sx={{ mt: 3 }}>
                    <Button
                      type='submit'
                      variant='contained'
                      disabled={!isFormValid || disableButton}
                      fullWidth
                      sx={{ height: 40 }}
                    >
                      CONNECT TELEMATICS
                    </Button>
                  </Box>

                  {/* Help button */}
                  <Box sx={{ width: '100%', textAlign: 'center', mt: 2 }}>
                    <Button
                      onClick={handleHelpClick}
                      sx={{
                        color: primaryColor,
                        textTransform: 'none',
                        fontSize: { xs: '13px', sm: '14px' },
                        fontWeight: 400,
                        textDecoration: 'underline',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                        },
                        padding: '8px 4px',
                        minHeight: 'auto',
                      }}
                    >
                      Need help getting connected?
                    </Button>
                  </Box>
                </Box>
              </Box>
            </FormContainer>
          </Box>

          {/* Footer with Terms & Privacy */}
          <DirectFooter
            serviceName={serviceName}
            primaryColor={primaryColor}
            footerBgColor={theme.palette?.background?.default || '#F5F6F7'}
          />
        </Paper>
      </Box>

      {/* Help Modal */}
      <HelpModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        helpText={TSPHelpText}
        tsp={tsp || ''}
        serviceName={serviceName}
      />

      {/* Skip Confirmation Dialog */}
      <SkipConfirmation
        open={skipDialogOpen}
        onClose={handleCloseSkipDialog}
        onSkip={handleSkipConfirmed}
        serviceName={serviceName}
      />
    </>
  );
}
